import { Point, Polygon, MultiPolygon } from 'ol/geom';
import { getCenter, getHeight, getWidth } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';

import { uniqueBy, uniqueValuesBy, isNum } from '@utils/helpers';

const FALLBACK_COLOR = 'rgba(255, 0, 0, 1)';

/** Object Map of OpenLayers Geometry Types */
export const GEOMETRY = { Point, Polygon, MultiPolygon };

/** Returns the GeoJSON format with proper projection settings */
export const getFormat = projection =>
	new GeoJSON({
		defaultDataProjection: projection,
		defaultFeatureProjection: projection,
		dataProjection: projection,
		featureProjection: projection,
	});

/** Converts a feature object into an OpenLayers Feature */
export const createFeature = (geometryObject, properties) => {
	try {
		const { type, coordinates } = geometryObject;
		const geometry = new GEOMETRY[type](coordinates);
		return new Feature({ ...properties, geometry });
	} catch (error) {
		console.group('Failed To Create Feature');
		console.log({ geometryObject, properties });
		console.error(error);
		console.groupEnd();
		return null;
	}
};


/** Converts color object to rgba string */
export const convertToRgba = color => {
	if (!color) return null;
	const { r, g, b, a } = color;
	return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/** Returns the color expression for features as defined in colorScheme */
export const getColorSchemeStyle = (colorScheme, forceOpacity = false) => {
	const matches = Object.entries(colorScheme).flatMap(([key, { color }]) => {
		if (forceOpacity) color = { ...color, a: forceOpacity };
		return [`${key}`, convertToRgba(color)];
	});

	if (!matches.length) return FALLBACK_COLOR;
	else return ['match', ['get', 'classid'], ...matches, FALLBACK_COLOR];
};

export const getTrainingDataStyle = (classInfo, forceOpacity = false) => {
	const matches = Object.entries(classInfo).flatMap(([key, { color }]) => {
		if (forceOpacity) color = { ...color, a: forceOpacity };
		return [`${key}`, convertToRgba(color)];
	});

	if (!matches.length) return FALLBACK_COLOR;
	else return ['match', ['get', 'classname'], ...matches, FALLBACK_COLOR];
};

/** Returns the point radius expression (Adjusts based on zoom level) */
export const getPointRadius = (
	radiusRange = { min: 1.5, max: 5 },
	zoomRange = { min: 5, max: 20 }
) => [
	'interpolate',
	['exponential', 2],
	['zoom'],
	zoomRange.min,
	radiusRange.min,
	zoomRange.max,
	radiusRange.max,
];

/** Ensures no overlap with stroke & fill */
export const noOverlapOffset = strokeWidth => Math.round(strokeWidth / 2) * -1 + 1;

/** Sets up the detection details for the application */
export const setupDetectionDetails = (detectionsData, colorOptions) => {
	const result = {
		detections: [],
		modelClasses: [],
		colorScheme: {},
	};

	for (const detectionData of detectionsData) {
		const { properties: rawProperties, geometry: latestGeometry } = detectionData;
		const { center, classid, bbox, area, bbox_area, last_model_used, radius } = rawProperties;
		const { x, y } = center;

		const properties = {
			...rawProperties,
			classid: String(classid), // Ensure classid is a string
			area: last_model_used == 'bbox' ? bbox_area : area, // Use most accurate area
			diameter: radius * 2, // Make diameter available
		};

		const geometries = {
			center: { type: 'Point', coordinates: [x, y] },
			outline: latestGeometry,
			bounding_box: { type: 'Polygon', coordinates: bbox },
		};

		// Add detections to the result array
		result.detections.push({ properties, geometries });
	}

	result.modelClasses = uniqueBy(
		detectionsData.map(detection => ({
			id: detection.properties.classid,
			label: detection.properties.classname,
		})),
		detection => `${detection.id}-${detection.label}`
	).sort((a, b) => Number(a.id) - Number(b.id));

	// Using the detectionId
	//  - to get the correct color from the colorOptions
	//  - as the key for the colorScheme object to make it easier to find the color for a given detectionId
	const classIds = uniqueValuesBy(detectionsData, detection => detection.properties.classid);
	result.colorScheme = classIds.reduce((acc, detectionId) => {
		acc[detectionId] = {
			color: colorOptions[detectionId % Object.keys(colorOptions).length],
			visible: true,
		};
		return acc;
	}, {});

	return result;
};

/** Filters detections based on colorScheme and detectionsFilter */
export const filterDetections = (detections, colorScheme, detectionsFilter) =>
	detections.filter(({ properties }) => {
		const { classid, classname } = properties;
		const { visible, color } = colorScheme[classid] ?? {};

		// Filter out invisible
		if (!visible || color?.a == 0) return false;

		// If no filter for this class, show all
		const filter = detectionsFilter[classname];
		if (!filter) return true;

		// Filter example:
		// { confidence: { min: 0.5, max: 1 }, height: { min: 1, max: 10 } }
		for (const [key, { min, max }] of Object.entries(filter)) {
			const featureValue = properties[key];

			// If value not a number, can't filter it, so exclude by default
			if (!isNum(featureValue)) return false;

			// Filter out value outside of range
			if (featureValue < min || featureValue > max) return false;
		}

		// Passed all filters
		return true;
	});

/** Creates OL features from detections */
export const getFeatures = detections => {
	const features = {
		center: [],
		outline: [],
		bounding_box: [],
	};
	// Add OL Features of custom geometry types to the result object.

	try {
		for (const detection of detections) {
			const { properties, geometries } = detection;
			for (const [name, geometry] of Object.entries(geometries)) {
				const feature = createFeature(geometry, { ...properties, featureType: name });
				if (feature) features[name].push(feature);
			}
		}
	} catch (error) {
		console.group('Error Creating Features');
		console.error(error);
		console.groupEnd();
	}

	return features;
};

export const deleteLayerByCustomId = (mapObject, customLayerId) => {
	if (!mapObject) return;

	const isLayerDeleted = mapObject
		.getLayers()
		.getArray()
		.some(layer => {
			const layerCustomId = layer.getProperties()?.customLayerId;
			if (layerCustomId === customLayerId) {
				mapObject.removeLayer(layer);
				return true;
			}
			return false;
		});

	return isLayerDeleted;
};

export const getLayerByCustomId = (mapObject, customLayerId) => {
	if (!mapObject) return;

	const layer = mapObject
		.getLayers()
		.getArray()
		.find(layer => {
			const layerCustomId = layer.getProperties()?.customLayerId;
			return layerCustomId === customLayerId;
		});

	return layer;
};

export const setLayersVisibilityByCustomIds = (mapObject, customLayerIds, visibility) => {
	if (!mapObject) return;

	mapObject
		.getLayers()
		.getArray()
		.forEach(layer => {
			const layerCustomId = layer.getProperties()?.customLayerId;
			if (customLayerIds.includes(layerCustomId)) {
				layer.setVisible(visibility);
			}
		});
};

export const addMapLoadingSpinner = map => {
	if (!map) return;
	map.getTargetElement().classList.add('spinner');
};

export const removeMapLoadingSpinner = map => {
	if (!map) return;
	map.getTargetElement().classList.remove('spinner');
};

export const polygonPositionsToMapCoordinates = (imagePositionPolygon, imageExtent) => {
	const xLength = imageExtent[2] - imageExtent[0];
	const yLength = imageExtent[3] - imageExtent[1];

	return imagePositionPolygon.map(polygonPositions =>
		polygonPositions.map(position => {
			const xValue = xLength * position[0] + imageExtent[0];
			const yValue = yLength * (1 - position[1]) + imageExtent[1];

			return [xValue, yValue];
		})
	);
};

export const circlePositionToMapCoordinates = (imagePositionCircle, imageExtent) => {
	const xLength = imageExtent[2] - imageExtent[0];
	const yLength = imageExtent[3] - imageExtent[1];

	const xValue = xLength * imagePositionCircle[0] + imageExtent[0];
	const yValue = yLength * (1 - imagePositionCircle[1]) + imageExtent[1];

	return [xValue, yValue];
};

export const calculateGeometryCenter = geometry => {
	let center, coordinates, minRadius;
	const type = geometry.getType();
	if (type === 'Polygon') {
		let x = 0;
		let y = 0;
		let i = 0;
		coordinates = geometry.getCoordinates()[0].slice(1);
		coordinates.forEach(function (coordinate) {
			x += coordinate[0];
			y += coordinate[1];
			i++;
		});
		center = [x / i, y / i];
	} else if (type === 'LineString') {
		center = geometry.getCoordinateAt(0.5);
		coordinates = geometry.getCoordinates();
	} else {
		center = getCenter(geometry.getExtent());
	}
	let sqDistances;
	if (coordinates) {
		sqDistances = coordinates.map(function (coordinate) {
			const dx = coordinate[0] - center[0];
			const dy = coordinate[1] - center[1];
			return dx * dx + dy * dy;
		});
		minRadius = Math.sqrt(Math.max.apply(Math, sqDistances)) / 3;
	} else {
		minRadius = Math.max(getWidth(geometry.getExtent()), getHeight(geometry.getExtent())) / 3;
	}
	return {
		center: center,
		coordinates: coordinates,
		minRadius: minRadius,
		sqDistances: sqDistances,
	};
};

/**
 * Converts image position polygons to map coordinates.
 *
 * @param {Array} imagePositionPolygon - Array of polygons, where each polygon is an array of [x, y] positions in image coordinates.
 * @param {Array} imageExtent - Array representing the extent of the image in map coordinates [minX, minY, maxX, maxY].
 * @returns {Array} - Array of polygons, where each polygon is an array of [x, y] positions in map coordinates.
 */
export const imagePositionsToMapCoordinates = (imagePositionPolygon, imageExtent) => {
	// Calculate the width and height of the image extent
	const xLength = imageExtent[2] - imageExtent[0];
	const yLength = imageExtent[3] - imageExtent[1];

	// Map each polygon in the image position polygon array
	return imagePositionPolygon.map(polygonPositions =>
		// Map each position in the polygon
		polygonPositions.map(position => {
			// Calculate the x and y values in map coordinates
			const xValue = xLength * position[0] + imageExtent[0];
			const yValue = yLength * (1 - position[1]) + imageExtent[1];

			// Return the new position in map coordinates
			return [xValue, yValue];
		})
	);
};

export const preventClickOnOverlay = event => {
	const target = event?.originalEvent?.target;
	if (target.closest('.ol-overlaycontainer')) return true;
	return false;
};

/**
 * Safe error logger that doesn't expose internal details in production
 *
 * @param {string} context - Context/component name
 * @param {string} message - Error message
 * @param {Error} error - Error object (optional)
 */
export const logError = (context, message, error = null) => {
	if (process.env.NODE_ENV === 'production') {
		// In production, log minimal info
		console.error(`[${context}] ${message}`);
	} else {
		// In development, log full details
		console.error(`[${context}] ${message}`, error || '');
	}
};
